<template>
  <div id="app" :class="{ 'with-menu': showChrome && menuOpen }">

    <!-- The hallway display runs full screen with no header and no menu: it has
         no navigation, and nobody signs out from a wall. -->
    <template v-if="showChrome">
      <AppHeader :menu-open="menuOpen" @toggle-menu="toggleMenu" />
      <AppMenu :open="menuOpen" @close="closeMenu" />
    </template>

    <div class="page">
      <router-view />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import AppHeader from "@/components/AppHeader.vue"
import AppMenu from "@/components/AppMenu.vue"
import { authState, role } from "@/auth/authService"

// Whether to show the header and menu is a question about the viewer, not the
// page. The dashboard used to be chromeless for everyone, which left a normal
// user on their own landing page with no navigation at all.
//
// Only the wall screen, signed in as the display role, gets the bare version.
const showChrome = computed(() =>
  Boolean(authState.account) && role() !== 'display'
)

// Open by default. The burger is the only thing that closes it, and the choice
// is remembered per browser so it stays as the person left it.
//
// It used to default to closed below 900px, on the reasoning that a sidebar
// leaves no room on a narrow screen. That made it appear shut for no reason
// anybody could see, on a window that was merely a bit small. Width is a poor
// proxy for what someone wants.
//
// localStorage rather than the server: this is about one screen, not the
// person, and a laptop and a large monitor may reasonably differ.
const STORAGE_KEY = 'vero.menu.open'

function initialState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved !== null) return saved === 'true'
  } catch {
    // Private browsing, or storage disabled. Falling through to the default is
    // better than failing to render.
  }

  return true
}

const menuOpen = ref(initialState())

watch(menuOpen, (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // Nothing useful to do; the menu still works for this session.
  }
})

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function closeMenu() {
  menuOpen.value = false
}
</script>

<style>
#app {
  font-family: "Space Grotesk", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

body {
  margin: 0;
}

/* The page shifts rather than the sidebar overlapping it, so both are usable at
   once. Below the breakpoint the sidebar floats over the page instead, because
   shifting would leave nothing to read. */
.page {
  transition: padding-left 0.22s ease;
}

@media (min-width: 900px) {
  .with-menu .page {
    padding-left: 260px;
  }
}

/* The header stays full width and does NOT shift with the menu: the burger has
   to stay reachable at the left edge, and moving it would put it on top of the
   sidebar it opens. */

@media (prefers-reduced-motion: reduce) {
  .page { transition: none; }
}
</style>
